/**
 * Shared primitives used by AppTour and AccessibilityTour.
 *
 * Kept in one file so the spotlight rendering, card layout and Sheets-write
 * logic stay in sync across every tour in the app.
 */

import { ChevronRight, X } from 'lucide-react'
import type { ElementType } from 'react'
import { useTranslation } from 'react-i18next'
import { postCompletions } from '@/api/progress-client'
import { cn } from '@/lib/utils'
import { LangToggle } from '@/player/lang-toggle'
import { UNSET_CONTENT_REVISION } from '@/player/lesson-progress'
import { getSessionIdentity } from '@/session/identity'

// ─── Layout constants ─────────────────────────────────────────────────────────

export const SPOTLIGHT_PAD = 10
export const SHOW_DELAY_MS = 600
export const SCROLL_SETTLE_MS = 450
const CARD_ESTIMATE_H = 230
const CARD_PAD = 12

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Rect {
  top: number
  left: number
  width: number
  height: number
}

export interface TourStepMeta {
  id: string
  /** data-app-tour attribute value on the element to spotlight. Absent = full-screen overlay. */
  target?: string
  Icon: ElementType
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function getElementRect(target: string): Rect | null {
  const el = document.querySelector(`[data-app-tour="${target}"]`)
  if (!el) return null
  const r = el.getBoundingClientRect()
  return { top: r.top, left: r.left, width: r.width, height: r.height }
}

/** Top offset for the card when a spotlight is active. Prefers below; falls back above. */
export function cardTop(spotlight: Rect): number {
  const spaceBelow = window.innerHeight - (spotlight.top + spotlight.height) - SPOTLIGHT_PAD
  const belowY = spotlight.top + spotlight.height + SPOTLIGHT_PAD + 8
  if (spaceBelow >= CARD_ESTIMATE_H) return belowY
  const aboveY = spotlight.top - SPOTLIGHT_PAD - CARD_ESTIMATE_H
  return Math.max(CARD_PAD, aboveY)
}

/**
 * Fire-and-forget write to `POST /api/progress/completions`.
 * Never throws. Fails silently if identity is absent or network is down.
 */
export function postTourCompletion(moduleId: string, subModuleId: string): void {
  const identity = getSessionIdentity()
  if (!identity) return
  void postCompletions({
    employeeId: identity.employeeId,
    roleId: identity.roleId,
    contentRevision: UNSET_CONTENT_REVISION,
    events: [
      {
        clientEventId: crypto.randomUUID(),
        moduleId,
        subModuleId,
        completedAt: new Date().toISOString(),
        stage: 'guided',
        requiresUnguided: false,
      },
    ],
  })
}

// ─── Spotlight ────────────────────────────────────────────────────────────────

export function Spotlight({ rect }: Readonly<{ rect: Rect }>) {
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        top: rect.top - SPOTLIGHT_PAD,
        left: rect.left - SPOTLIGHT_PAD,
        width: rect.width + SPOTLIGHT_PAD * 2,
        height: rect.height + SPOTLIGHT_PAD * 2,
        borderRadius: 14,
        boxShadow: '0 0 0 9999px rgba(0,0,0,0.78)',
        outline: '2px solid rgba(255,255,255,0.35)',
        pointerEvents: 'none',
        zIndex: 1,
      }}
    />
  )
}

// ─── Tour card ────────────────────────────────────────────────────────────────

export interface TourCardProps {
  Icon: ElementType
  title: string
  body: string
  stepIndex: number
  totalSteps: number
  stepIds: readonly string[]
  spotlightRect: Rect | null
  /** Label for the primary advance button on the last step. */
  lastStepLabel: string
  onBack: () => void
  onNext: () => void
  onSkip: () => void
}

export function TourCard({
  Icon,
  title,
  body,
  stepIndex,
  totalSteps,
  stepIds,
  spotlightRect,
  lastStepLabel,
  onBack,
  onNext,
  onSkip,
}: Readonly<TourCardProps>) {
  const { t } = useTranslation('player')
  const isFirst = stepIndex === 0
  const isLast = stepIndex === totalSteps - 1

  const positionStyle: React.CSSProperties = spotlightRect
    ? { top: cardTop(spotlightRect) }
    : { top: '50%', transform: 'translateY(-50%)' }

  return (
    <dialog
      aria-modal="true"
      aria-label={title}
      open
      className="absolute left-4 right-4 m-0 p-0 bg-card border border-border rounded-2xl shadow-2xl overflow-hidden"
      style={{ ...positionStyle, zIndex: 2 }}
    >
      {/* Header */}
      <div className="flex items-start gap-3 p-5 pb-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0 pt-0.5">
          <h2 className="text-base font-bold text-foreground leading-snug">{title}</h2>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <LangToggle />
          <button
            type="button"
            onClick={onSkip}
            aria-label={t('tour.skip')}
            className="w-7 h-7 rounded-full bg-muted flex items-center justify-center shrink-0 active:scale-95 transition-transform"
          >
            <X className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        </div>
      </div>

      {/* Body */}
      <p className="px-5 pb-4 text-sm text-muted-foreground leading-relaxed">{body}</p>

      {/* Progress dots */}
      <div className="flex items-center gap-1.5 px-5 pb-4" aria-hidden="true">
        {stepIds.map((id, i) => (
          <div
            key={id}
            className={cn(
              'h-1.5 rounded-full transition-all duration-200',
              i === stepIndex ? 'w-5 bg-primary' : 'w-1.5 bg-muted',
            )}
          />
        ))}
      </div>

      {/* Nav buttons */}
      <div className="flex gap-2 px-5 pb-5">
        {!isFirst && (
          <button
            type="button"
            onClick={onBack}
            className="flex-1 min-h-11 rounded-xl border border-border text-sm font-semibold text-foreground active:scale-[0.98] transition-transform"
          >
            {t('tour.back')}
          </button>
        )}
        <button
          type="button"
          onClick={onNext}
          className="flex-1 min-h-11 rounded-xl bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center gap-1.5 shadow-sm active:scale-[0.98] transition-transform"
        >
          {isLast ? lastStepLabel : t('tour.next')}
          {!isLast && <ChevronRight className="w-4 h-4" />}
        </button>
      </div>
    </dialog>
  )
}
